import type { BigIntish } from '@morpho-org/blue-sdk'
import type { ThemeMode } from '@morpho-org/ui-v6/hooks'

import { isDefined } from '@morpho-org/morpho-ts'
import { formatters, getLogoProxyImgProps } from '@morpho-org/ui-v6/utils'
import { isLogoProxySrc, toLogoSrc } from '@repo/utils'
import { formatISO9075 } from 'date-fns'
import Highcharts, {
  type FormatterCallbackFunction,
  type GradientColorObject,
  type Options,
  type OptionsStackingValue,
  type PatternObject,
  type PlotLineOrBand,
  type Point,
  type TooltipFormatterCallbackFunction
} from 'highcharts'
import { getImageProps } from 'next/image'
import { formatUnits } from 'viem'

import type { NumberDataPoint } from '@/types/enums'

import type { ChartTheme } from './chartTheme'

import { MIN_VISIBLE_ALLOCATION } from './colorAllocationSeries'

interface BigIntDataPoint {
  x: number
  y: BigIntish | null
}

type AnyPoint =
  | BigIntDataPoint
  | NumberDataPoint
  | { x: number; y: bigint | null | undefined }
  | { x: number; y: number | null | undefined }
  | [number, bigint | null | undefined]
  | [number, number | null | undefined]

function getX(point: AnyPoint) {
  if ('x' in point) {
    return point.x
  }
  return point[0]
}

function getY(point: AnyPoint, decimals?: number) {
  if ('y' in point && typeof point.y === 'bigint') {
    if (!decimals) {
      throw new Error('decimals is required for BigIntDataPoint')
    }
    return point.y !== undefined && point.y !== null ? Number(formatUnits(point.y, decimals)) : null
  }

  if ('y' in point && typeof point.y === 'string') {
    if (!decimals) {
      throw new Error('decimals is required for BigIntDataPoint')
    }
    return point.y !== undefined && point.y !== null
      ? Number(formatUnits(BigInt(point.y), decimals))
      : null
  }

  if ('y' in point && typeof point.y === 'number') {
    if (decimals) {
      return isDefined(point.y) ? Number(formatUnits(BigInt(point.y), decimals)) : null
    }

    return point.y
  }

  if (point instanceof Array && typeof point[1] === 'bigint') {
    if (!decimals) {
      throw new Error('decimals is required for BigIntDataPoint')
    }
    return point[1] !== undefined && point[1] !== null
      ? Number(formatUnits(point[1], decimals))
      : null
  }

  if (point instanceof Array && typeof point[1] === 'number') {
    if (decimals) {
      return isDefined(point[1]) ? Number(formatUnits(BigInt(point[1]), decimals)) : null
    }
    return point[1]
  }

  return null
}

function convertUnixTimestampToMs(timestamp: number): number {
  return timestamp * 1000
}

export function formatTimeseriesData(
  data: Array<[number, number | null | undefined]> | null | undefined
): [number, number | null][]
export function formatTimeseriesData(
  data: Array<[number, bigint | null | undefined]> | null | undefined
): [number, number | null][]
export function formatTimeseriesData(
  data: Array<{ x: number; y: bigint | null | undefined }> | null | undefined
): [number, number | null][]
export function formatTimeseriesData(
  data: Array<{ x: number; y: number | null | undefined }> | null | undefined
): [number, number | null][]
export function formatTimeseriesData(
  data: Array<NumberDataPoint> | null | undefined
): [number, number | null][]
export function formatTimeseriesData(
  data: Array<BigIntDataPoint> | null | undefined,
  decimals: number
): [number, number | null][]

/**
 * Formats timeseries data to be used in Highcharts.
 * Assumes that x value is a unix timestamp in seconds.
 *
 * @param data
 * @param decimals
 * @returns
 */
export function formatTimeseriesData(
  data:
    | Array<BigIntDataPoint>
    | Array<NumberDataPoint>
    | Array<{ x: number; y: bigint | null | undefined }>
    | Array<{ x: number; y: number | null | undefined }>
    | Array<[number, bigint | null | undefined]>
    | Array<[number, number | null | undefined]>
    | null
    | undefined,
  decimals?: number
): [number, number | null][] {
  if (!data || data.length === 0) {
    return []
  }

  // Highcharts may mutate the data, so we need to copy the original data to avoid side-effects.
  // https://github.com/highcharts/highcharts-react?tab=readme-ov-file#why-highcharts-mutates-my-data
  const seriesData: [number, number | null][] = data.map(point => {
    const x = getX(point)
    const timestampMs = convertUnixTimestampToMs(x)
    return [timestampMs, getY(point, decimals)]
  })

  // Highcharts requires the data to be sorted by x value in ascending order
  // https://assets.highcharts.com/errors/15/
  seriesData.sort((a, b) => a[0] - b[0])

  return seriesData
}

export function formatSeriesData(
  data: Array<[number, number | null | undefined]> | null | undefined
): [number, number | null][]
export function formatSeriesData(
  data: Array<[number, bigint | null | undefined]> | null | undefined
): [number, number | null][]
export function formatSeriesData(
  data: Array<{ x: number; y: bigint | null | undefined }> | null | undefined
): [number, number | null][]
export function formatSeriesData(
  data: Array<{ x: number; y: number | null | undefined }> | null | undefined
): [number, number | null][]
export function formatSeriesData(
  data: Array<NumberDataPoint> | null | undefined
): [number, number | null][]
export function formatSeriesData(
  data: Array<BigIntDataPoint> | null | undefined,
  decimals: number
): [number, number | null][]

/**
 * Formats series data to be used in Highcharts.
 *
 * @param data
 * @param decimals
 * @returns
 */
export function formatSeriesData(
  data:
    | Array<BigIntDataPoint>
    | Array<NumberDataPoint>
    | Array<{ x: number; y: bigint | null | undefined }>
    | Array<{ x: number; y: number | null | undefined }>
    | Array<[number, bigint | null | undefined]>
    | Array<[number, number | null | undefined]>
    | null
    | undefined,
  decimals?: number
): [number, number | null][] {
  if (!data || data.length === 0) {
    return []
  }

  // Highcharts may mutate the data, so we need to copy the original data to avoid side-effects.
  // https://github.com/highcharts/highcharts-react?tab=readme-ov-file#why-highcharts-mutates-my-data
  const seriesData: [number, number | null][] = data.map(point => [
    getX(point),
    getY(point, decimals)
  ])

  // Highcharts requires the data to be sorted by x value in ascending order
  // https://assets.highcharts.com/errors/15/
  seriesData.sort((a, b) => a[0] - b[0])

  return seriesData
}

interface XFormatterFunction {
  (point: Point): string | null
}
interface YFormatterFunction {
  (point: Point): string
}
type TooltipVariant = 'default' | 'large'
interface ThemeWithModeInput {
  theme: ChartTheme
  themeMode: ThemeMode
}

const defaultXFormatter = (point?: Point) =>
  formatISO9075(new Date(Number(point?.x)), {
    representation: 'date'
  })
const defaultYFormatter = (point?: Point) => String(point?.y)

type TooltipColor = string | GradientColorObject | PatternObject

const getColorFromGradient = (color?: TooltipColor) => {
  if (typeof color === 'object' && 'stops' in color) {
    return color.stops?.[0]?.[1]
  }
  return color
}

// Single swatch shape for every multi-series tooltip, so the allocation, cumulative
// and simple charts stay visually identical.
function renderSwatchDot(color?: TooltipColor): string {
  const resolved = getColorFromGradient(color)
  if (typeof resolved !== 'string') return ''
  return `<div style="width: 10px; height: 10px; flex-shrink: 0; border-radius: 100px; background-color: ${resolved};"></div>`
}

// In styled mode the series colour lives in CSS (`.highcharts-color-N` →
// `var(--series-N)`), so `point.color` is unreliable. Read the swatch colour back
// from the colorIndex as the same @repo `--series-*` var the chart is painted with,
// keeping the tooltip swatch in sync without any JS colour value.
const seriesSwatchVar = (point: Point): string | undefined =>
  typeof point.colorIndex === 'number' ? `var(--series-${point.colorIndex % 10})` : undefined

interface CreateTooltipFormatterInput extends ThemeWithModeInput {
  formatters: {
    x?: XFormatterFunction | null
    y?: Array<YFormatterFunction | null> | YFormatterFunction
    showSeriesColor?: boolean
    total?: (value: number) => string
  }
  variant?: TooltipVariant
}

export function createTooltipFormatter({
  formatters,
  theme,
  themeMode,
  variant = 'default'
}: CreateTooltipFormatterInput): TooltipFormatterCallbackFunction {
  return function () {
    const points = this.points ? this.points.map(p => p.point) : [this.point]

    const headerFormatter = formatters.x ?? defaultXFormatter

    const total = formatters.total ? formatters.total(this.point.total ?? 0) : null

    const headerText = headerFormatter(this.point) + (total ? ` - ${total}` : '')

    const rows = points.map((point, i) => {
      const series = point.series
      const name = series.name
      const color = seriesSwatchVar(point)

      let formatter: YFormatterFunction = defaultYFormatter
      if (formatters.y instanceof Array) {
        formatter = formatters.y[i] ?? defaultYFormatter
      } else if (typeof formatters.y === 'function') {
        formatter = formatters.y
      }

      return {
        formattedY: isDefined(point.y) ? formatter(point) : '-',
        name,
        color: formatters.showSeriesColor ? color : undefined
      }
    })

    return HTMLRenderSimpleChart({
      headerText,
      theme,
      themeMode,
      rows,
      variant
    })
  }
}

/**
 * HTML render for simple chart tooltip.
 *
 * @param headerText - The header text.
 * @param theme - The theme.
 * @param themeMode - The theme mode (light or dark).
 * @param rows - The rows.
 * @param variant - The size variant.
 */

interface HTMLRenderSimpleChartInput extends ThemeWithModeInput {
  headerText: string | null
  rows: {
    name: string
    formattedY: string
    color?: TooltipColor
  }[]
  variant?: TooltipVariant
}

export function HTMLRenderSimpleChart({
  headerText,
  theme,
  themeMode,
  rows,
  variant = 'default'
}: HTMLRenderSimpleChartInput) {
  return `
    <div style="
        display: flex;
        flex-flow: column;
        gap: ${theme.spacing.xs};
        padding: 10px;
        background-color: var(--popover);
        border-radius: 4px;
        ${variant === 'large' ? 'font-size: 12px;' : 'font-size: 11px;'}
        ${themeMode === 'light' ? getLightThemeTooltipStyles(theme) : ''}
      ">
      ${headerText ? `<div>${headerText}</div>` : ''}
      ${rows
        .map(
          ({ formattedY, color }) => `
          <div style="display: flex; flex-flow: row nowrap; gap: ${theme.spacing.xs}; align-items: center; z-index: ${theme.zIndex.dropdown.mid};">
            ${renderSwatchDot(color)}
            <div style="z-index: ${theme.zIndex.dropdown.mid};">${formattedY}</div>
          </div>`
        )
        .join('')}
      </div>
  `
}

export function createTooltipFormatterCumulativeChart({
  theme,
  themeMode
}: ThemeWithModeInput): TooltipFormatterCallbackFunction {
  return function () {
    const points = this.points ? this.points.map(p => p.point) : [this.point]
    const headerText = defaultXFormatter(points[0]!)
    const pointSum = points.reduce((acc, point) => acc + (point.y ?? 0), 0)

    const rows = points.map(point => {
      const series = point.series
      const color = seriesSwatchVar(point)
      const name = series.name

      const percentage = point.y ? point.y / pointSum : 0
      const formattedY = formatters.percent
        .unit('%')
        .digits(2)
        .default('0')
        .trailingZero(false)
        .of(percentage)

      return {
        formattedY,
        name,
        color
      }
    })

    return HTMLRenderCumulativeChart({
      headerText,
      theme,
      themeMode,
      rows
    })
  }
}

/**
 * HTML render for cumulative chart tooltip.
 *
 * @param headerText - The header text.
 * @param theme - The theme.
 * @param themeMode - The theme mode (light or dark).
 * @param rows - The rows.
 *
 */

interface HTMLRenderCumulativeChartInput extends ThemeWithModeInput {
  headerText: string | null
  rows: {
    name: string
    color: TooltipColor | undefined
    formattedY: string
  }[]
}

export function HTMLRenderCumulativeChart({
  headerText,
  theme,
  themeMode,
  rows
}: HTMLRenderCumulativeChartInput) {
  return `
    <div style="
        width: 300px;
        display: flex;
        flex-flow: column;
        gap: ${theme.spacing.s};
        padding: ${theme.padding.s};
        background-color: var(--popover);
        border: 1px solid var(--border);
        border-radius: 20px;
        box-shadow: ${theme.boxShadow.dp4};
        ${themeMode === 'light' ? getLightThemeTooltipStyles(theme) : ''}
      ">
      ${headerText && `<div style="font-size: 11px;">${headerText}</div>`}
      ${rows
        .map(
          ({ name, color, formattedY }) => `
          <div style="display: flex; gap: 4px; align-items: center; z-index: ${theme.zIndex.dropdown.mid}">
            ${renderSwatchDot(color)}
            <div style="z-index: ${theme.zIndex.dropdown.mid}; display: flex; justify-content: space-between; width: 100%">
              <div style="font-size: 12px;">${name}</div>
              <div style="font-size: 12px;">${formattedY}</div>
            </div>
          </div>`
        )
        .join('')}
      </div>
  `
}

export interface AllocationSeriesCustomData {
  marketName: string
  formattedLltv: string | null
  loanAssetLogoURI: string | null
  collateralAssetLogoURI: string | null
  isIdle: boolean
}

interface CreateTooltipFormatterAllocationChartInput extends ThemeWithModeInput {
  stackingMode: OptionsStackingValue
  assetSymbol?: string
}

export function createTooltipFormatterAllocationChart({
  theme,
  themeMode,
  stackingMode,
  assetSymbol
}: CreateTooltipFormatterAllocationChartInput): TooltipFormatterCallbackFunction {
  return function () {
    const points = this.points ?? []
    const headerText = defaultXFormatter(points[0]?.point)
    const byValue = points
      .filter(p => !!p.y && p.y >= MIN_VISIBLE_ALLOCATION)
      .toSorted((a, b) => (b.y ?? 0) - (a.y ?? 0))

    const visible =
      stackingMode === 'percent'
        ? (() => {
            const sum = byValue.reduce((acc, p) => acc + (p.y ?? 0), 0)
            return byValue.filter(p => {
              const pct = (p.y ?? 0) / sum
              return Math.round(pct * 10000) > 0
            })
          })()
        : byValue

    const visibleSum = visible.reduce((acc, p) => acc + (p.y ?? 0), 0)

    const rows = visible.map(p => {
      // oxlint-disable-next-line typescript/consistent-type-assertions
      const custom = p.series.options.custom as AllocationSeriesCustomData | undefined
      const percentage = visibleSum > 0 ? (p.y ?? 0) / visibleSum : 0

      const formattedValue =
        stackingMode === 'normal'
          ? formatters.short
              .unit(assetSymbol ?? '')
              .digits(2)
              .of(p.y ?? 0)
          : formatters.percent.unit('%').digits(2).trailingZero(false).of(percentage)

      return {
        marketName: custom?.marketName ?? p.series.name,
        formattedLltv: custom?.formattedLltv ?? null,
        loanAssetLogoURI: custom?.loanAssetLogoURI ?? null,
        collateralAssetLogoURI: custom?.collateralAssetLogoURI ?? null,
        isIdle: custom?.isIdle ?? false,
        color: seriesSwatchVar(p.point),
        formattedValue
      }
    })

    return HTMLRenderAllocationChart({
      headerText,
      theme,
      themeMode,
      rows
    })
  }
}

interface AllocationChartRow {
  marketName: string
  formattedLltv: string | null
  loanAssetLogoURI: string | null
  collateralAssetLogoURI: string | null
  isIdle: boolean
  color: TooltipColor | undefined
  formattedValue: string
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

const TOOLTIP_LOGO_PX = 16
const OPTIMIZED_IMAGE_ORIGIN = 'https://cdn.morpho.org/'

// Highcharts sanitises useHTML tooltips through its AST allowlist, which drops `srcset`.
// Highcharts is a factory function (no AST) until it runs in a browser.
if (typeof Highcharts === 'object' && !Highcharts.AST.allowedAttributes.includes('srcset')) {
  Highcharts.AST.allowedAttributes.push('srcset')
}

export function getTooltipLogoImgAttrs(logoURI: string): {
  src: string
  srcSet: string | undefined
} {
  // Many cdn.morpho.org SVGs are raster-in-SVG wrappers too heavy for the browser;
  // the logo proxy rasterizes them into an app-relative path the optimizer serves.
  const src = toLogoSrc(logoURI)
  if (isLogoProxySrc(src)) {
    const props = getLogoProxyImgProps(src, TOOLTIP_LOGO_PX)
    return { src: props.src, srcSet: props.srcSet }
  }
  const { props } = getImageProps({
    src,
    alt: '',
    // Request 2× the rendered size, like Avatar does for non-logo images, so 3× screens
    // get a sharp 2x candidate.
    width: TOOLTIP_LOGO_PX * 2,
    height: TOOLTIP_LOGO_PX * 2,
    // cdn.morpho.org is the sole images.remotePatterns host and app-relative
    // paths resolve same-origin; the optimizer 400s anything else.
    unoptimized: !(
      src.startsWith(OPTIMIZED_IMAGE_ORIGIN) ||
      (src.startsWith('/') && !src.startsWith('//'))
    )
  })
  return { src: props.src, srcSet: props.srcSet }
}

function renderAvatarImg(logoURI: string | null, extraStyle?: string): string {
  if (!logoURI) return ''
  const { src, srcSet } = getTooltipLogoImgAttrs(logoURI)
  const srcSetAttr = srcSet ? ` srcset="${escapeHtml(srcSet)}"` : ''
  return `<img src="${escapeHtml(src)}"${srcSetAttr} width="${TOOLTIP_LOGO_PX}" height="${TOOLTIP_LOGO_PX}" style="display: block; min-width: ${TOOLTIP_LOGO_PX}px; min-height: ${TOOLTIP_LOGO_PX}px; border-radius: 50%; object-fit: contain;${extraStyle ?? ''}" />`
}

function renderPlaceholderAvatar(theme: ChartTheme, extraStyle?: string): string {
  return `<div style="width: ${TOOLTIP_LOGO_PX}px; height: ${TOOLTIP_LOGO_PX}px; min-width: ${TOOLTIP_LOGO_PX}px; min-height: ${TOOLTIP_LOGO_PX}px; border-radius: 50%; background-color: var(--border);${extraStyle ?? ''}"></div>`
}

function renderAvatarImgOrPlaceholder(
  logoURI: string | null,
  theme: ChartTheme,
  extraStyle?: string
): string {
  if (logoURI) return renderAvatarImg(logoURI, extraStyle)
  return renderPlaceholderAvatar(theme, extraStyle)
}

function renderBadge(text: string): string {
  return `<span style="padding: 1px 6px; border-radius: 10px; font-size: 10px; background-color: var(--muted); color: var(--secondary-foreground);">${escapeHtml(text)}</span>`
}

function HTMLRenderAllocationChart({
  headerText,
  theme,
  themeMode,
  rows
}: {
  headerText: string
  theme: ChartTheme
  themeMode: ThemeMode
  rows: AllocationChartRow[]
}) {
  return `
    <div style="
      width: 320px;
      display: flex;
      flex-flow: column;
      gap: ${theme.spacing.xs};
      padding: ${theme.padding.s};
      background-color: var(--popover);
      border: 1px solid var(--border);
      border-radius: 20px;
      box-shadow: ${theme.boxShadow.dp4};
      font-size: 11px;
      ${themeMode === 'light' ? getLightThemeTooltipStyles(theme) : ''}
    ">
      ${headerText ? `<div>${escapeHtml(headerText)}</div>` : ''}
      ${rows
        .map(
          ({
            marketName,
            formattedLltv,
            loanAssetLogoURI,
            collateralAssetLogoURI,
            isIdle,
            color,
            formattedValue
          }) => {
            const isMarket = formattedLltv !== null
            const avatars = isIdle
              ? renderAvatarImg(loanAssetLogoURI)
              : isMarket
                ? // Loan asset leads, matching the loan-first market identity.
                  `${renderAvatarImgOrPlaceholder(loanAssetLogoURI, theme)}${renderAvatarImgOrPlaceholder(collateralAssetLogoURI, theme, ' margin-left: -10px;')}`
                : renderAvatarImgOrPlaceholder(loanAssetLogoURI, theme)

            const badge = isIdle
              ? renderBadge('Idle')
              : formattedLltv
                ? renderBadge(formattedLltv)
                : ''

            return `
            <div style="display: flex; gap: 6px; align-items: center;">
              ${renderSwatchDot(color)}
              <div style="display: inline-flex; align-items: center; flex-shrink: 0;${isMarket ? ' min-width: 22px;' : ''}">${avatars}</div>
              <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0;">${escapeHtml(marketName)}</div>
              ${badge}
              <div style="margin-left: auto; white-space: nowrap; flex-shrink: 0;">${escapeHtml(formattedValue)}</div>
            </div>`
          }
        )
        .join('')}
    </div>
  `
}

export function getHighchartModule() {
  if (typeof Highcharts !== 'object') {
    // Highcharts is not available yet.
    // https://github.com/highcharts/highcharts-react?tab=readme-ov-file#highcharts-with-nextjs
    return null
  }
  return Highcharts
}

export function mergeOptions(baseOptions: Options, ...options: Options[]): Options {
  return getHighchartModule()?.merge(baseOptions, ...options) ?? {}
}

export function createPlotlineLabelFormatter(
  colorLabel?: string
): FormatterCallbackFunction<PlotLineOrBand> {
  return function () {
    const text = this.options.label?.text
    if (!text) return ''
    return renderTextLabel(text, colorLabel)
  }
}

function renderTextLabel(label: string, colorLabel?: string) {
  return `<div style="
      border: 0.5px solid var(--border);
      border-radius: 16px;
      padding: 4px 10px;
      font-size: 11px;
      background-color: var(--popover);
      color: ${colorLabel ? colorLabel : 'var(--secondary-foreground)'};
    ">
      ${label}
    </div>`
}

function getLightThemeTooltipStyles(theme: ChartTheme) {
  return `
    box-shadow: ${theme.boxShadow.dp2};
    border: 0.5px solid var(--border);
  `
}

/**
 *  Add a point to the beginning of the series if there is only one point
 *  Push one point at x - 1, y = 0 in series with one point
 *
 * @param options
 * @returns options
 *
 * @example
 * ```ts
 * const options = {
 *  series: [
 *   {
 *    data: [[1, 1]],
 *  },
 * ],
 * };
 * const newOptions = pushPoint0InSeriesWithOnePoint(options);
 * // newOptions.series[0].data ==> [[0, 0], [1, 1]]
 * ```
 **/
export function pushPoint0InSeriesWithOnePoint(options: Options) {
  let hasOnePoint = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Highcharts' SeriesOptionsType union doesn't expose a uniform `data` array to read/mutate here
  options.series = options.series?.map((serie: any) => {
    if (serie.data.length === 1) {
      hasOnePoint = true
      serie.data = [[serie.data[0][0] - 1, 0], ...serie.data]
    }
    return serie
  })

  // X axis become specific date format if there is only one point and one series
  if (hasOnePoint && options.series?.length === 1) {
    options = mergeOptions(options, {
      xAxis: {
        type: 'datetime',
        labels: {
          format: '{value:%Y-%m-%d}'
        }
      }
    })
    return options
  }

  return options
}
/**
 * Remove null y values from series
 *
 * @param series
 * @returns series
 */
export const removeNullYValuesFromSeries = (
  series: [number, number | null][]
): [number, number][] => {
  // oxlint-disable-next-line typescript/consistent-type-assertions
  return series.filter(value => value[1] !== null) as [number, number][]
}

/**
 * Check if a series is empty
 *
 * @param series
 * @returns boolean
 */

export function isSerieEmpty(series: [number, number | null][]): boolean {
  return series.every(value => value[1] === 0 || value[1] === null)
}
